import React, { useState } from "react";
import { Chip } from "@mui/material";
import { colors } from "../../../styles/colors";

interface ToggleChipProps {
  /** Aktueller Wert (true = aktiv/ja, false = inaktiv/nein) */
  value: boolean;
  /** Label für aktiven Zustand */
  activeLabel?: string;
  /** Label für inaktiven Zustand */
  inactiveLabel?: string;
  /** Callback beim Klick (Toggle) */
  onToggle: (e: React.MouseEvent) => void;
  /** Chip deaktiviert */
  disabled?: boolean;
}

/**
 * Toggle Chip mit lokalem Hover-State.
 *
 * Hover-Effekt: Chip wird etwas größer und wechselt zwischen filled/outlined,
 * um dem User eine Vorschau des Toggle-Ergebnisses zu geben.
 *
 * Der lokale Hover-State verhindert, dass Parent-Komponenten (z.B. Tabellen)
 * bei jedem Mouse-Enter/Leave komplett neu rendern.
 */
const ToggleChip: React.FC<ToggleChipProps> = ({
  value,
  activeLabel = "Ja",
  inactiveLabel = "Nein",
  onToggle,
  disabled = false,
}) => {
  const [isHovered, setIsHovered] = useState(false);

  // Beim Hover: Variante umschalten (filled ↔ outlined) als Vorschau
  const variant = value
    ? isHovered
      ? "outlined"
      : "filled"
    : isHovered
      ? "filled"
      : "outlined";

  return (
    <Chip
      label={value ? activeLabel : inactiveLabel}
      size="small"
      variant={variant}
      onClick={onToggle}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      disabled={disabled}
      sx={{
        minWidth: "60px",
        cursor: disabled ? "default" : "pointer",
        transition:
          "transform 0.2s ease-in-out, background-color 0.2s ease-in-out, border-color 0.2s ease-in-out",
        // Bewusst klein: Der Chip ist so breit wie seine Rasterspalte, die Vergrößerung wirkt
        // sich also absolut aus und nicht relativ zu einem kleinen Symbol. Bei 1.15 ragte er
        // über das Feld rechts daneben.
        transform: isHovered && !disabled ? "scale(1.02)" : "scale(1)",
        // Aktiver Zustand: Teal-Farben
        ...(value
          ? {
              backgroundColor: isHovered ? "transparent" : colors.active.main,
              color: isHovered ? colors.active.dark : colors.text.onDark,
              border: `1px solid ${colors.active.main}`,
            }
          : {
              // Inaktiver Zustand: beim Hover gefüllt als Vorschau
              ...(isHovered
                ? {
                    backgroundColor: colors.active.light,
                    color: colors.active.dark,
                    border: `1px solid ${colors.active.main}`,
                  }
                : {}),
            }),
      }}
    />
  );
};

export default ToggleChip;
